import {createContext, Fragment, useEffect, useReducer} from "react";
import {Spinner} from "@hipo/react-ui-toolkit";

import {algod} from "../../../core/integrations/algosdk/AlgodManager";
import useReconnectPeraWallet from "../../../core/integrations/pera-wallet/useReconnectPeraWallet";
import useInterval from "../../../core/util/hook/useInterval";
import {
  getSelectedAccountAddressFromWebStorage,
  getWalletAccountsFromWebStorage,
  getWalletTypeFromWebStorage,
  handleDisconnectSuccessful,
  isPeraWallet,
  isValidWalletType,
  mapAccountsToAppStateAccounts,
  resetWalletItemsOnWebStorage
} from "../walletUtils";
import {
  ACCOUNT_DATA_POLLING_INTERVAL,
  initialWalletContextValue
} from "./WalletContext.constants";
import walletContextReducer from "./WalletContext.reducer";
import {WalletContextReducerAction} from "./WalletContext.types";
import {fetchAndSetAccountData} from "./WalletContext.utils";
import useAsyncProcess from "../../../core/network/async-process/useAsyncProcess";
import {SwapWidgetSearchParamKey} from "../../../core/widgetTypes";
import useWalletConnectUpdateHandler from "../../../core/integrations/wallet-connect/useWalletConnectUpdateHandler";
import walletConnectManager from "../../../core/integrations/wallet-connect/WalletConnect.manager";

const WalletContext = createContext<typeof initialWalletContextValue | undefined>(
  undefined
);
const WalletContextDispatch = createContext<React.Dispatch<WalletContextReducerAction>>(
  () => undefined
);

WalletContext.displayName = "WalletContext";
WalletContextDispatch.displayName = "WalletContextDispatch";

interface WalletContextProviderProps {
  children: React.ReactNode;
}

function WalletContextProvider({children}: WalletContextProviderProps) {
  const [state, dispatch] = useReducer(walletContextReducer, initialWalletContextValue);

  const {state: initialAsyncProcessState, runAsyncProcess} = useAsyncProcess();
  const searchParams = new URLSearchParams(document.location.search);
  const addressFromUrl = searchParams.get(SwapWidgetSearchParamKey.ACCOUNT_ADDRESS);
  const accountAddress = addressFromUrl || state.account?.address || null;
  const reconnectPeraWallet = useReconnectPeraWallet(dispatch);
  const walletConnectUpdateHandler = useWalletConnectUpdateHandler(dispatch);

  useEffect(() => {
    runAsyncProcess(
      fetchInitialData().catch((error) => {
        resetWalletItemsOnWebStorage();
        console.error("Initial data fetching has failed");
        console.error(error);
      })
    );

    async function fetchInitialData() {
      const storedWalletType = getWalletTypeFromWebStorage();
      const isPeraWalletStored = isPeraWallet(storedWalletType);
      const shouldTryReconnectWallet = isPeraWalletStored;

      if (shouldTryReconnectWallet) {
        try {
          if (isPeraWalletStored) {
            await reconnectPeraWallet();
          }
        } catch (error) {
          resetWalletItemsOnWebStorage();
        }
      }

      const storedAddress = getSelectedAccountAddressFromWebStorage();
      const storedWalletAddresses = getWalletAccountsFromWebStorage();
      const hasValidWalletType = isValidWalletType(storedWalletType);

      let shouldFetchSelectedAccountData = Boolean(storedAddress && hasValidWalletType);

      if (
        storedAddress &&
        storedWalletAddresses?.length &&
        storedWalletType &&
        hasValidWalletType
      ) {
        const walletAccounts = await Promise.all(
          storedWalletAddresses.map((address) =>
            algod.client.accountInformation(address).do()
          )
        );
        const finalAccountsData = await mapAccountsToAppStateAccounts(walletAccounts);

        const selectedAccount = finalAccountsData.find(
          (account) => account.detail.address === storedAddress
        );

        if (selectedAccount) {
          shouldFetchSelectedAccountData = false;

          dispatch({
            type: "SET_WALLET_AND_ACCOUNTS",
            accounts: finalAccountsData,
            walletType: storedWalletType,
            selectedAccount
          });
        }
      } else {
        resetWalletItemsOnWebStorage();
      }

      if (shouldFetchSelectedAccountData && storedAddress) {
        fetchAndSetAccountData(dispatch, storedAddress, 0);
      }
    }
  }, [reconnectPeraWallet, runAsyncProcess]);

  useInterval(() => {
    if (accountAddress) {
      fetchAndSetAccountData(dispatch, accountAddress, 0);
    }
  }, ACCOUNT_DATA_POLLING_INTERVAL);

  useEffect(() => {
    (async () => {
      await walletConnectManager.initialize({
        onDisconnectSuccessful: () => handleDisconnectSuccessful(dispatch),
        onSessionUpdate: walletConnectUpdateHandler
      });
    })();
  }, [walletConnectUpdateHandler]);

  return (
    <WalletContext.Provider value={state}>
      <WalletContextDispatch.Provider value={dispatch}>
        {renderChildren()}
      </WalletContextDispatch.Provider>
    </WalletContext.Provider>
  );

  function renderChildren() {
    let node;

    if (initialAsyncProcessState.isRequestPending) {
      node = (
        <div className={"app__initial-async-state-wrapper is-centered"}>
          <Spinner />
        </div>
      );
    } else if (initialAsyncProcessState.isRequestFetched) {
      node = <Fragment>{children}</Fragment>;
    }

    return node;
  }
}

export default WalletContext;
export {WalletContextDispatch, WalletContextProvider};
